import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { DateTime } from 'luxon';

import config from '@/config';
import type { Issue } from '@/constants';
import { cognitoHostedUrl, readCognitoAccount } from '@shared/auth';
import { checkForNewBuild, type BuildCheckResult } from '@shared/sw';
import {
	requestPersistentStorage,
	useStorageEstimate,
} from '@shared/lib/hooks';
import { formatMegabytes } from '@shared/lib/format-bytes';
import { exportDebugZip } from '@shared/lib/debug-log';
import { Logger } from '@shared/lib/logger';
import { useInstallAction } from '@features/install-pwa';
import { T, useLanguage, useT, useUiDirection, type MessageKey } from '@shared/i18n';
import { useClearAppData } from '@features/clear-app-data';
import { clearReaderStats } from '@entities/reader-stats';
import {
	useDownloadedIssues,
	useIssueStorage,
} from '@entities/downloaded-issue';

import type { SettingControlProps } from './setting-controls';
import styles from './settings-dialog.module.css';

const logger = new Logger('settings');

/**
 * The Settings rows that show information or run an action rather than
 * hold a preference: Account, Offline & storage, About, and the keyboard
 * shortcuts. Merged into `SETTING_CONTROLS` by id.
 */
export const SETTING_PANELS: Record<
	string,
	(props: SettingControlProps) => ReactNode
> = {
	account: AccountPanel,
	subscription: SubscriptionPanel,
	'storage-usage': StorageUsagePanel,
	downloads: DownloadsPanel,
	'clear-cache': ClearCachePanel,
	'clear-reading-stats': ClearReadingStatsPanel,
	'app-version': AppVersionPanel,
	'check-updates': CheckUpdatesPanel,
	'install-app': InstallAppPanel,
	'legal-links': LegalLinksPanel,
	'debug-info': DebugInfoPanel,
	'keyboard-shortcuts': KeyboardShortcutsPanel,
};

/** A button that runs the row's action, named "<label>: <button text>". */
function ActionButton({
	labelId,
	children,
	onClick,
	disabled,
}: {
	labelId?: string;
	children: ReactNode;
	onClick: () => void;
	disabled?: boolean;
}) {
	return (
		<button
			type="button"
			className={styles['action']}
			onClick={onClick}
			disabled={disabled}
			aria-describedby={labelId}
		>
			{children}
		</button>
	);
}

// ---- Account ------------------------------------------------------------

function AccountPanel({ labelId }: SettingControlProps) {
	const t = useT();
	const { basepath } = useRouter();
	const account = readCognitoAccount();
	if (!account) {
		return null;
	}
	if (account.isAdmin) {
		return <span className={styles['value']}>{t('settings.account.editor')}</span>;
	}
	if (account.isAuthenticated) {
		return (
			<div className={styles['actions']}>
				<span className={styles['value']}>{t('settings.account.signedIn')}</span>
				<ActionButton
					labelId={labelId}
					onClick={() => {
						if (confirm(t('common.signOutConfirm'))) {
							location.href = cognitoHostedUrl('logout', basepath);
						}
					}}
				>
					{t('common.signOut')}
				</ActionButton>
			</div>
		);
	}
	return (
		<div className={styles['actions']}>
			<ActionButton
				labelId={labelId}
				onClick={() => {
					location.href = cognitoHostedUrl('signup', basepath);
				}}
			>
				{t('settings.account.signUp')}
			</ActionButton>
			<ActionButton
				labelId={labelId}
				onClick={() => {
					location.href = cognitoHostedUrl('login', basepath);
				}}
			>
				{t('settings.account.signIn')}
			</ActionButton>
		</div>
	);
}

function SubscriptionPanel({ labelId }: SettingControlProps) {
	const t = useT();
	const navigate = useNavigate();
	const account = readCognitoAccount();
	if (!account?.customerPortalUrl) {
		return null;
	}
	if (!account.isAuthenticated) {
		return <span className={styles['value']}>
				{t('settings.subscription.signInFirst')}
			</span>;
	}
	if (!account.isSubscribed) {
		return (
			<ActionButton
				labelId={labelId}
				onClick={() => navigate({ to: '/subscriptions' })}
			>
				{t('common.subscribe')}
			</ActionButton>
		);
	}
	const portal = account.customerPortalUrl;
	return (
		<ActionButton
			labelId={labelId}
			onClick={() => {
				open(portal, '_blank', 'noopener');
			}}
		>
			{t('settings.subscription.manage')}
		</ActionButton>
	);
}

// ---- Offline & storage --------------------------------------------------

function StorageUsagePanel({ labelId }: SettingControlProps) {
	const t = useT();
	const { usage, quota, refresh } = useStorageEstimate();
	const [persisted, setPersisted] = useState<boolean | null>(null);

	useEffect(() => {
		if (!('storage' in navigator) || !('persisted' in navigator.storage)) {
			return;
		}
		navigator.storage.persisted().then(setPersisted, () => setPersisted(null));
	}, []);

	const used =
		usage !== undefined && usage < 0.1 * 1024 * 1024
			? t('settings.storageUsage.tiny')
			: formatMegabytes(usage ?? 0);
	const bold = (chunks: ReactNode) => <strong>{chunks}</strong>;

	return (
		<div className={styles['panel']}>
			{usage !== undefined ? (
				<p>
					{quota ? (
						<T
							k="settings.storageUsage.usedOf"
							values={{ used, available: formatMegabytes(quota) }}
							components={{ b: bold }}
						/>
					) : (
						<T
							k="settings.storageUsage.used"
							values={{ used }}
							components={{ b: bold }}
						/>
					)}
				</p>
			) : (
				<p>{t('settings.storageUsage.unknown')}</p>
			)}
			{persisted === true ? (
				<p>{t('settings.storageUsage.persisted')}</p>
			) : persisted === false ? (
				<div className={styles['actions']}>
					<p>{t('settings.storageUsage.mayClear')}</p>
					<ActionButton
						labelId={labelId}
						onClick={() => {
							void requestPersistentStorage().then((granted) => {
								setPersisted(granted);
								refresh();
							});
						}}
					>
						{t('settings.storageUsage.keep')}
					</ActionButton>
				</div>
			) : null}
		</div>
	);
}

function DownloadsPanel() {
	const t = useT();
	const { isLoading, issues, removingIssueId, remove } = useDownloadedIssues();
	if (isLoading) {
		return null;
	}
	if (!issues.length) {
		return <p className={styles['panel']}>{t('settings.downloads.empty')}</p>;
	}
	return (
		<ul className={styles['list']}>
			{issues.map((issue) => (
				<DownloadRow
					key={issue.id}
					issue={issue}
					isRemoving={removingIssueId === issue.id}
					onRemove={() => remove(issue.id, { confirmed: true })}
				/>
			))}
		</ul>
	);
}

/**
 * Remove confirms inline ("Remove" → "Remove?" / "Cancel") rather than
 * through the drawer's toast: clicking a toast from inside this modal counts
 * as clicking outside it and closes Settings.
 */
function DownloadRow({
	issue,
	isRemoving,
	onRemove,
}: {
	issue: Issue;
	isRemoving: boolean;
	onRemove: () => void;
}) {
	const { data: storage } = useIssueStorage(issue.id);
	const t = useT();
	const [confirming, setConfirming] = useState(false);
	return (
		<li>
			{issue.cover ? <img src={issue.cover} alt="" loading="lazy" /> : null}
			<span className={styles['list-text']}>
				<span dir="auto">{issue.name}</span>
				<span>{storage ? formatMegabytes(storage.totalBytes) : ' '}</span>
			</span>
			{confirming ? (
				<span className={styles['actions']}>
					<button
						type="button"
						className={styles['action']}
						data-danger
						disabled={isRemoving}
						onClick={onRemove}
						aria-label={t('settings.downloads.confirmRemove', { name: issue.name })}
					>
						{t('settings.downloads.remove')}
					</button>
					<button
						type="button"
						className={styles['action']}
						onClick={() => setConfirming(false)}
					>
						{t('common.cancel')}
					</button>
				</span>
			) : (
				<button
					type="button"
					className={styles['action']}
					onClick={() => setConfirming(true)}
					aria-label={t('settings.downloads.removeIssue', { name: issue.name })}
				>
					{t('settings.downloads.remove')}
				</button>
			)}
		</li>
	);
}

function ClearCachePanel({ labelId }: SettingControlProps) {
	const t = useT();
	const clearAppData = useClearAppData();
	return (
		<ActionButton
			labelId={labelId}
			onClick={() => {
				void clearAppData();
			}}
		>
			{t('settings.clearCache.label')}
		</ActionButton>
	);
}

/**
 * Deletes the reader's own figures. The event log is left alone: what is
 * still waiting to go to the publisher's analytics is governed by the
 * Analytics switch, not by this.
 */
function ClearReadingStatsPanel({ labelId }: SettingControlProps) {
	const t = useT();
	return (
		<ActionButton
			labelId={labelId}
			onClick={() => {
				void clearReaderStats();
			}}
		>
			{t('settings.clearReadingStats.label')}
		</ActionButton>
	);
}

// ---- About --------------------------------------------------------------

function AppVersionPanel() {
	const { version, buildNumber } = config.build;
	const t = useT();
	// Re-render (and re-format the date) when the language changes.
	useLanguage();
	// buildNumber is a Unix epoch (seconds) timestamp -- see
	// scripts/prepare.js's getBuildId().
	const built = DateTime.fromSeconds(buildNumber).toLocaleString(
		DateTime.DATE_MED,
	);
	return (
		<span className={styles['value']} title={t('settings.appVersion.build', { number: buildNumber })}>
			{version} · {built}
		</span>
	);
}

const UPDATE_MESSAGES: Record<BuildCheckResult, MessageKey> = {
	updating: 'settings.checkUpdates.updating',
	current: 'settings.checkUpdates.current',
	offline: 'settings.checkUpdates.offline',
	failed: 'settings.checkUpdates.failed',
};

function CheckUpdatesPanel({ labelId }: SettingControlProps) {
	const t = useT();
	const [status, setStatus] = useState<BuildCheckResult | 'checking' | null>(
		null,
	);
	return (
		<div className={styles['actions']}>
			<output className={styles['value']}>
				{status === 'checking'
					? t('settings.checkUpdates.checking')
					: status
						? t(UPDATE_MESSAGES[status])
						: null}
			</output>
			<ActionButton
				labelId={labelId}
				disabled={status === 'checking' || status === 'updating'}
				onClick={() => {
					setStatus('checking');
					void checkForNewBuild().then(setStatus);
				}}
			>
				{t('settings.checkUpdates.button')}
			</ActionButton>
		</div>
	);
}

function InstallAppPanel({ labelId }: SettingControlProps) {
	const t = useT();
	const { show, onClick } = useInstallAction();
	if (!show) {
		return (
			<span className={styles['value']}>
				{t('settings.installApp.installed')}
			</span>
		);
	}
	return (
		<ActionButton labelId={labelId} onClick={onClick}>
			{t('settings.installApp.button')}
		</ActionButton>
	);
}

function LegalLinksPanel() {
	const { legal } = config.app;
	const links = [legal?.tou, legal?.privacy, legal?.cookie].filter(
		(link): link is NonNullable<typeof link> => !!link?.url,
	);
	return (
		<ul className={styles['links']}>
			{links.map((link) => (
				<li key={link.url}>
					<a href={link.url} target="_blank" rel="noreferrer">
						{link.label}
					</a>
				</li>
			))}
		</ul>
	);
}

function DebugInfoPanel({ labelId }: SettingControlProps) {
	const t = useT();
	return (
		<ActionButton
			labelId={labelId}
			onClick={() => {
				exportDebugZip().catch((error: unknown) => {
					logger.error('Failed to export debug info', error);
				});
			}}
		>
			{t('settings.debugInfo.button')}
		</ActionButton>
	);
}

// ---- Accessibility ------------------------------------------------------

/** The keys the app binds (`useHotkey` in the reader and the homes). */
const SHORTCUTS: ReadonlyArray<{ keys: Array<string>; action: MessageKey }> = [
	{ keys: ['Shift', 'M'], action: 'settings.shortcut.menu' },
	{ keys: ['Shift', 'T'], action: 'settings.shortcut.toc' },
	{ keys: ['←'], action: 'settings.shortcut.previous' },
	{ keys: ['→'], action: 'settings.shortcut.next' },
	{ keys: ['Esc'], action: 'settings.shortcut.close' },
];

function KeyboardShortcutsPanel() {
	const t = useT();
	// The reader swaps ← and → in a right-to-left language.
	const rtl = useUiDirection() === 'rtl';
	const swap = (key: string) =>
		rtl && key === '←' ? '→' : rtl && key === '→' ? '←' : key;
	return (
		<dl className={styles['shortcuts']}>
			{SHORTCUTS.map(({ keys, action }) => (
				<div key={action}>
					<dt>{t(action)}</dt>
					<dd>
						{keys.map((key, index) => (
							<span key={key}>
								{index > 0 ? ' + ' : null}
								<kbd>{swap(key)}</kbd>
							</span>
						))}
					</dd>
				</div>
			))}
		</dl>
	);
}
